/**
 * TermsPage - Terms of Service & Acceptable Use Policy for NLT Radio.
 * Includes an explicit "Prohibited Content & Conduct" section required for
 * Stripe / Stripe Connect payment-processing compliance.
 */
import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Ban, Copyright, FileWarning, HeartCrack } from 'lucide-react';
import Footer from '../components/Footer';

const LAST_UPDATED = 'June 2026';

const prohibited = [
  {
    icon: Ban,
    title: 'Adult content & services',
    color: '#FF007F',
    points: [
      'No nudity, pornography, sexually explicit images, audio, or video.',
      'No depiction or facilitation of explicit sexual acts.',
      'No subscriber-only, pay-to-unlock, or "premium" nude or sexual content.',
      'No adult live chat, sexual cam services, escort, or other adult services.',
      'No sexualized content involving minors of any kind (this is also reported to authorities under our zero-tolerance CSAM policy).',
    ],
  },
  {
    icon: Copyright,
    title: 'Intellectual property & proprietary rights infringement',
    color: '#00F0FF',
    points: [
      'You may only upload, sell, or monetize content that you own or are fully licensed to use.',
      'You may not monetize music, artwork, video, merchandise, logos, names, or likenesses that you do not have the rights to.',
      'You must hold all necessary rights, licenses, and clearances (including samples, features, and cover art) for anything you distribute or sell on NLT Radio.',
      'Impersonating another artist, brand, or rights holder is prohibited.',
    ],
  },
  {
    icon: FileWarning,
    title: 'Copyright-infringing content',
    color: '#FFB800',
    points: [
      'No uploading or selling leaked, ripped, or unauthorized music, albums, or tracks.',
      'No pre-release or bootleg recordings you are not authorized to distribute.',
      'No content that infringes any third party\u2019s copyright, trademark, or other rights.',
      'We respond to valid DMCA / takedown notices and will remove infringing content and terminate repeat infringers.',
    ],
  },
  {
    icon: HeartCrack,
    title: 'Violent extremism & hate speech',
    color: '#FF4D4D',
    points: [
      'No content that promotes, celebrates, incites, or facilitates unlawful violence or terrorism.',
      'No violent extremist propaganda, recruitment, or glorification of violent acts.',
      'No hate speech or content that promotes, incites, or celebrates hatred, harassment, or violence against any individual or group based on race, ethnicity, national origin, religion, disability, age, gender, gender identity, or sexual orientation.',
      'No threats of violence or content intended to intimidate or degrade a protected group.',
    ],
  },
];

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-24">
    <h2 className="text-xl sm:text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Unbounded' }}>{title}</h2>
    <div className="text-[#D4D4D8] text-sm sm:text-base leading-relaxed space-y-3">{children}</div>
  </section>
);

const TermsPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="terms-page">
      {/* Top bar */}
      <div className="sticky top-0 z-20 bg-[#050505]/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-4xl mx-auto flex items-center gap-3 px-4 py-3">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/10 rounded-lg transition" data-testid="terms-back-btn">
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <span className="text-white font-semibold">Terms & Acceptable Use</span>
        </div>
      </div>

      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#00F0FF]/10 via-transparent to-[#FF007F]/10 pointer-events-none" />
        <div className="relative max-w-3xl mx-auto px-5 pt-12 pb-6 text-center">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
            className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
            <ShieldCheck className="w-8 h-8 text-[#00F0FF]" />
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl font-bold text-white leading-tight" style={{ fontFamily: 'Unbounded' }}>
            Terms of Service &<br /><span className="bg-gradient-to-r from-[#00F0FF] to-[#FF007F] bg-clip-text text-transparent">Acceptable Use</span>
          </motion.h1>
          <p className="text-[#71717A] text-sm mt-4">Last updated: {LAST_UPDATED}</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 py-8 space-y-10">
        <Section title="1. Agreement">
          <p>
            Welcome to NLT Radio ("NLT Radio," "we," "us"). By creating an account, streaming, uploading,
            selling, purchasing, or otherwise using NLT Radio (the "Platform"), you agree to these Terms of
            Service and the Acceptable Use Policy below. If you do not agree, do not use the Platform.
          </p>
          <p>
            NLT Radio is a platform where independent artists own their story, connect directly with fans, and
            sell music, merchandise, tickets, and other offerings. You are responsible for the content you post,
            distribute, or sell, and for ensuring it complies with all applicable laws and these Terms.
          </p>
        </Section>

        <Section title="2. Eligibility & accounts">
          <p>
            You must be at least 18 years old (or the age of majority in your jurisdiction) to sell content,
            receive payouts, or use paid features. You are responsible for the accuracy of your account
            information and for keeping your credentials secure.
          </p>
        </Section>

        {/* The compliance-critical section */}
        <section id="prohibited" className="scroll-mt-24">
          <div className="flex items-center gap-2 mb-2">
            <Ban className="w-6 h-6 text-[#FF007F]" />
            <h2 className="text-xl sm:text-2xl font-bold text-white" style={{ fontFamily: 'Unbounded' }}>
              3. Prohibited Content & Conduct (Acceptable Use Policy)
            </h2>
          </div>
          <p className="text-[#D4D4D8] text-sm sm:text-base leading-relaxed mb-6">
            To keep NLT Radio safe and to comply with our payment processors, the following content, conduct,
            services, and offerings are strictly prohibited on the Platform, including in uploads, streams,
            clips, stories, live rooms, direct messages, marketplace listings, event galleries, and profiles.
            Violations may result in immediate content removal, withheld or reversed payouts, account
            suspension or termination, and reporting to the appropriate authorities.
          </p>

          <div className="space-y-4">
            {prohibited.map((cat) => (
              <div key={cat.title} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5" data-testid={`prohibited-${cat.title.split(' ')[0].toLowerCase()}`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${cat.color}1A` }}>
                    <cat.icon className="w-5 h-5" style={{ color: cat.color }} />
                  </span>
                  <h3 className="text-white font-semibold text-base sm:text-lg">{cat.title}</h3>
                </div>
                <ul className="space-y-2">
                  {cat.points.map((p, i) => (
                    <li key={i} className="flex gap-2 text-[#D4D4D8] text-sm leading-relaxed">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color }} />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <p className="text-[#A1A1AA] text-sm leading-relaxed mt-6">
            We also prohibit harassment, spam, fraud, sale of illegal or regulated goods (e.g. drugs, weapons,
            counterfeit items), and any other unlawful activity. This list is not exhaustive; we may remove any
            content or terminate any account that we reasonably believe violates these Terms, the law, or the
            requirements of our payment processors.
          </p>
        </section>

        <Section title="4. Payments, payouts & the artist wallet">
          <p>
            Payments and payouts are processed by our third-party payment providers (including Stripe). By
            selling on the Platform or receiving payouts, you also agree to the applicable payment provider
            terms, and you represent that your content and offerings comply with this Acceptable Use Policy.
          </p>
          <p>
            We may withhold, delay, or reverse payouts, and refuse or refund transactions, where we or our
            payment processors reasonably suspect a violation of these Terms, fraud, chargeback abuse, or
            unlawful activity.
          </p>
        </Section>

        <Section title="5. Intellectual property & takedowns">
          <p>
            You retain ownership of the content you create and upload, and you grant NLT Radio a license to
            host, stream, display, and distribute it for the purpose of operating the Platform. You are solely
            responsible for holding all rights necessary to upload, distribute, and monetize your content.
          </p>
          <p>
            We comply with the Digital Millennium Copyright Act (DMCA) and similar laws. To report infringing
            content, contact us with the details of the work and the allegedly infringing material. We remove
            infringing content and terminate repeat infringers.
          </p>
        </Section>

        <Section title="6. Enforcement">
          <p>
            We may investigate suspected violations and take any action we deem appropriate, including removing
            content, limiting features, withholding payouts, and suspending or terminating accounts, with or
            without notice. Serious violations (such as CSAM or credible threats of violence) are reported to
            law enforcement.
          </p>
        </Section>

        <Section title="7. Changes & contact">
          <p>
            We may update these Terms from time to time; continued use of the Platform after changes means you
            accept the updated Terms. Questions about these Terms or reports of prohibited content can be sent
            to our support team via the Help Center.
          </p>
          <p className="text-[#71717A] text-xs mt-4">
            This document is provided for transparency and platform compliance and does not constitute legal
            advice.
          </p>
        </Section>
      </div>

      <Footer />
    </div>
  );
};

export default TermsPage;
